import { useMutation, useQuery } from "@tanstack/react-query";
import { apiPost, apiGet } from "@/lib/api-client";

interface SaveRollerConfigBody {
  rollerApiUrl: string;
  rollerClientId: string;
  rollerClientSecret: string;
}

interface SaveRollerConfigResponse {
  success: boolean;
  data: {
    id: string;
    venueId: string;
    rollerApiUrl: string;
    rollerClientId: string;
  };
}

interface TestRollerConnectionResponse {
  success: boolean;
  data: {
    connected: boolean;
    staffCount?: number;
    error?: string;
  };
}

interface SyncRollerDataResponse {
  success: boolean;
  data: {
    staff: unknown[];
    revenue: unknown[];
    syncedAt: string;
  };
}

interface RollerIntegrationConfig {
  success: boolean;
  data: {
    id: string;
    venueId: string;
    rollerApiUrl: string;
    rollerClientId: string;
    createdAt: string;
    updatedAt: string;
  };
}

export function useSaveRollerConfig(venueId: string) {
  return useMutation({
    mutationFn: async (body: SaveRollerConfigBody) => {
      const response = await apiPost<SaveRollerConfigResponse>(
        `/venues/${venueId}/roller/config`,
        body as unknown as Record<string, unknown>
      );
      return response.data;
    },
  });
}

export function useTestRollerConnection(venueId: string) {
  return useMutation({
    mutationFn: async () => {
      const response = await apiPost<TestRollerConnectionResponse>(
        `/venues/${venueId}/roller/test`,
        {}
      );
      return response.data;
    },
  });
}

export function useSyncRollerData(venueId: string) {
  return useMutation({
    mutationFn: async () => {
      const response = await apiPost<SyncRollerDataResponse>(
        `/venues/${venueId}/roller/sync`,
        {}
      );
      return response.data;
    },
  });
}

export function useRollerIntegration(venueId: string | undefined) {
  return useQuery({
    queryKey: ["roller-integration", venueId],
    queryFn: async () => {
      if (!venueId) return null;
      const response = await apiGet<RollerIntegrationConfig>(
        `/venues/${venueId}/roller/config`
      );
      return response.data;
    },
    enabled: !!venueId,
  });
}
